import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ShieldCheck, XCircle } from "lucide-react";
import memberApi from "@/lib/memberApi";

/**
 * Landing page for the magic link. Reads ?token=mml_... from the email,
 * calls /api/community/auth to set the session cookie, then plays a warm
 * welcome animation and redirects to /community.
 */
export default function MagicLogin() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "loading", name: "", error: "" });

  useEffect(() => {
    const token = params.get("token") || "";
    if (!token) {
      setState({ status: "error", name: "", error: "לינק לא תקין" });
      return;
    }
    (async () => {
      try {
        const { data } = await memberApi.get(`/community/auth?token=${encodeURIComponent(token)}`);
        setState({ status: "success", name: data.name || "" });
        setTimeout(() => navigate("/community", { replace: true }), 1800);
      } catch (err) {
        const msg = err?.response?.status === 410
          ? "הלינק פג תוקף. בקש/י מייל חדש."
          : "הלינק לא נמצא. נסה/י מהודעת מייל אחרת או צור/י קשר.";
        setState({ status: "error", name: "", error: msg });
      }
    })();
  }, [params, navigate]);

  return (
    <div dir="rtl" className="min-h-screen bg-stone-950 flex items-center justify-center px-6 relative overflow-hidden">
      {/* subtle amber pulse background */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-[500px] h-[500px] rounded-full bg-amber-600/10 blur-3xl animate-pulse" />
      </div>

      <div className="relative z-10 max-w-lg w-full text-center">
        {state.status === "loading" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            data-testid="magic-loading"
          >
            <Loader2 className="w-14 h-14 text-amber-600 animate-spin mx-auto mb-6" />
            <div className="text-xl font-[Rubik] font-semibold text-stone-200">מזהה את הכניסה שלך…</div>
          </motion.div>
        )}

        {state.status === "success" && (
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            data-testid="magic-success"
          >
            <motion.div
              initial={{ scale: 0 }} animate={{ scale: 1 }}
              transition={{ delay: 0.15, type: "spring", stiffness: 220, damping: 12 }}
              className="w-20 h-20 mx-auto mb-6 rounded-full bg-amber-600/20 border border-amber-600/50 flex items-center justify-center"
            >
              <ShieldCheck className="w-10 h-10 text-amber-500" />
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.4 }}
              className="font-[Rubik] font-black text-3xl sm:text-4xl text-stone-50 mb-3"
            >
              ברוכים השבים
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45, duration: 0.4 }}
              className="text-lg text-stone-300 mb-2"
            >
              {state.name ? `${state.name}, ` : ""}לאזור הצמיחה שלך
            </motion.p>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.4 }}
              className="text-sm text-stone-500 mt-8"
            >
              מעביר/ה אותך פנימה…
            </motion.div>
          </motion.div>
        )}

        {state.status === "error" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            data-testid="magic-error"
            className="bg-stone-900 border border-red-800/40 rounded-2xl p-8"
          >
            <XCircle className="w-14 h-14 text-red-500 mx-auto mb-4" />
            <div className="font-[Rubik] font-bold text-xl text-stone-50 mb-2">לא הצלחנו להיכנס</div>
            <div className="text-sm text-stone-400 leading-relaxed">{state.error}</div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
